/**
 * Opérations de mutation.
 *
 * # Pourquoi des opérations et non des lignes complètes
 *
 * C'est le point central du modèle de synchronisation (cf. CLAUDE.md § 3.2).
 * Le client n'envoie jamais une ligne entière : il envoie une opération qui ne
 * touche que les champs concernés.
 *
 * Si le client envoyait la ligne complète, un appareil qui coche « lait »
 * renverrait aussi sa copie **périmée** du libellé, écrasant la correction faite
 * entre-temps par l'autre appareil. Ce serait le problème du blob JSON,
 * réintroduit à l'échelle de la ligne.
 *
 * Avec des opérations par champ :
 *
 *   Pixel  : item.rename('lait', 'Lait demi-écrémé')  → touche label, quantity
 *   iPhone : item.check('lait', true)                 → touche is_checked
 *
 * Les deux survivent. Le last-write-wins ne s'applique qu'entre opérations
 * touchant les **mêmes** champs.
 *
 * # Idempotence
 *
 * Aucune opération ne dépend de l'état antérieur de la ligne (`item.check` porte
 * la valeur cible, pas une bascule). Rejouer deux fois la même opération produit
 * donc le même état — ce qui autorise le client à rejouer sa file en cas de
 * doute, et rend inutile toute table d'événements.
 */

import type { Device, Item, List, Platform } from './types.ts';
import { normalisePlatform } from './types.ts';

export const MAX_LABEL_LENGTH = 200;
export const MAX_QUANTITY_LENGTH = 60;
export const MAX_NAME_LENGTH = 80;

/** Champs communs à toute opération. */
interface OpBase {
  /** UUID de l'opération. Sert à l'acquittement, pas à la déduplication. */
  op_id: string;
  /**
   * Horloge du client. Ne sert QU'À ordonner la file locale et à afficher un
   * horodatage plausible avant acquittement. Le serveur ne lui accorde aucune
   * autorité : il réhorodate tout (cf. CLAUDE.md § 3.1).
   */
  client_ts: number;
}

export type ItemOp =
  | (OpBase & {
      kind: 'item.create';
      id: string;
      list_id: string;
      label: string;
      quantity: string | null;
      position: number;
      device_id: string;
    })
  | (OpBase & { kind: 'item.rename'; id: string; label: string; quantity: string | null })
  | (OpBase & { kind: 'item.check'; id: string; is_checked: boolean; device_id: string })
  | (OpBase & { kind: 'item.move'; id: string; list_id: string; position: number })
  | (OpBase & { kind: 'item.delete'; id: string });

export type ListOp =
  | (OpBase & { kind: 'list.create'; id: string; name: string; position: number; is_default: boolean })
  | (OpBase & { kind: 'list.rename'; id: string; name: string })
  | (OpBase & { kind: 'list.move'; id: string; position: number })
  | (OpBase & { kind: 'list.setDefault'; id: string })
  | (OpBase & { kind: 'list.delete'; id: string });

export type DeviceOp =
  | (OpBase & { kind: 'device.register'; id: string; label: string; platform: Platform })
  | (OpBase & { kind: 'device.rename'; id: string; label: string });

export type Op = ItemOp | ListOp | DeviceOp;

export interface ApplyContext {
  /** Horodatage à écrire. Le serveur passe Date.now(), le client op.client_ts. */
  now: number;
  household_id: string;
}

export function isItemOp(op: Op): op is ItemOp {
  return op.kind.startsWith('item.');
}
export function isListOp(op: Op): op is ListOp {
  return op.kind.startsWith('list.');
}
export function isDeviceOp(op: Op): op is DeviceOp {
  return op.kind.startsWith('device.');
}

/** Identifiant de la ligne visée par l'opération. */
export function targetId(op: Op): string {
  return op.id;
}

// ---------------------------------------------------------------------------
// Application
//
// Chaque fonction est pure et ne touche qu'une seule ligne. Renvoyer `null`
// signifie « opération non applicable » : la ligne visée n'existe pas encore.
// Le serveur signale alors l'opération comme ignorée, et le client la conserve
// jusqu'à ce que le `create` correspondant soit passé.
// ---------------------------------------------------------------------------

export function applyItemOp(current: Item | null, op: ItemOp, ctx: ApplyContext): Item | null {
  if (op.kind === 'item.create') {
    // Upsert : rejouer un create déjà appliqué ne doit rien casser. On conserve
    // la ligne existante en ne remontant que updated_at.
    if (current) return { ...current, updated_at: ctx.now };
    return {
      id: op.id,
      household_id: ctx.household_id,
      list_id: op.list_id,
      label: clamp(op.label, MAX_LABEL_LENGTH),
      quantity: op.quantity === null ? null : clamp(op.quantity, MAX_QUANTITY_LENGTH),
      is_checked: false,
      position: op.position,
      created_at: ctx.now,
      created_by_device: op.device_id,
      updated_at: ctx.now,
      checked_at: null,
      checked_by_device: null,
      deleted_at: null,
    };
  }

  if (!current) return null;

  switch (op.kind) {
    case 'item.rename':
      return {
        ...current,
        label: clamp(op.label, MAX_LABEL_LENGTH),
        quantity: op.quantity === null ? null : clamp(op.quantity, MAX_QUANTITY_LENGTH),
        updated_at: ctx.now,
        // Toute opération ressuscite l'article : réapparaître par erreur est
        // moins grave que disparaître (cf. CLAUDE.md § 3.6).
        deleted_at: null,
      };

    case 'item.check':
      return {
        ...current,
        is_checked: op.is_checked,
        // On horodate aussi le décochage : « décoché par X » reste explicable.
        checked_at: ctx.now,
        checked_by_device: op.device_id,
        updated_at: ctx.now,
        deleted_at: null,
      };

    case 'item.move':
      return {
        ...current,
        list_id: op.list_id,
        position: op.position,
        updated_at: ctx.now,
        deleted_at: null,
      };

    case 'item.delete':
      return { ...current, deleted_at: ctx.now, updated_at: ctx.now };
  }
}

export function applyListOp(current: List | null, op: ListOp, ctx: ApplyContext): List | null {
  if (op.kind === 'list.create') {
    if (current) return { ...current, updated_at: ctx.now };
    return {
      id: op.id,
      household_id: ctx.household_id,
      name: clamp(op.name, MAX_NAME_LENGTH),
      position: op.position,
      is_default: op.is_default,
      created_at: ctx.now,
      updated_at: ctx.now,
      deleted_at: null,
    };
  }

  if (!current) return null;

  switch (op.kind) {
    case 'list.rename':
      return { ...current, name: clamp(op.name, MAX_NAME_LENGTH), updated_at: ctx.now, deleted_at: null };

    case 'list.move':
      return { ...current, position: op.position, updated_at: ctx.now, deleted_at: null };

    case 'list.setDefault':
      // On ne retire PAS le drapeau des autres listes. L'exclusivité est
      // résolue à la lecture (cf. resolveDefaultList) : c'est une écriture au
      // lieu de deux, et surtout aucune opération multi-lignes — donc aucun
      // conflit possible hors ligne.
      return { ...current, is_default: true, updated_at: ctx.now, deleted_at: null };

    case 'list.delete':
      return { ...current, deleted_at: ctx.now, updated_at: ctx.now };
  }
}

export function applyDeviceOp(current: Device | null, op: DeviceOp, ctx: ApplyContext): Device | null {
  if (op.kind === 'device.register') {
    if (current) return { ...current, updated_at: ctx.now };
    return {
      id: op.id,
      household_id: ctx.household_id,
      label: clamp(op.label, MAX_NAME_LENGTH),
      platform: normalisePlatform(op.platform),
      created_at: ctx.now,
      updated_at: ctx.now,
    };
  }

  if (!current) return null;
  // La plateforme n'est jamais réévaluée : détectée une fois à l'inscription,
  // c'est ensuite le label saisi par l'utilisateur qui fait foi.
  return { ...current, label: clamp(op.label, MAX_NAME_LENGTH), updated_at: ctx.now };
}

/**
 * Choisit la liste par défaut.
 *
 * Plusieurs listes peuvent porter `is_default` simultanément — c'est le prix de
 * l'écriture mono-ligne de `list.setDefault`. La plus récemment marquée gagne,
 * ce qui est cohérent avec le last-write-wins appliqué partout ailleurs.
 */
export function resolveDefaultList(lists: readonly List[]): List | null {
  const alive = lists.filter((l) => l.deleted_at === null);
  if (alive.length === 0) return null;

  const flagged = alive.filter((l) => l.is_default);
  const pool = flagged.length > 0 ? flagged : alive;

  return pool.reduce((best, l) => {
    if (l.updated_at !== best.updated_at) return l.updated_at > best.updated_at ? l : best;
    return l.id < best.id ? l : best;
  });
}

/**
 * Nettoyage des textes. Exporté parce que le serveur SQL doit appliquer
 * exactement la même règle que l'application en mémoire : sans quoi un libellé
 * s'afficherait différemment avant et après acquittement.
 */
export function clampText(value: string, max: number): string {
  const trimmed = value.trim();
  return trimmed.length <= max ? trimmed : trimmed.slice(0, max);
}

const clamp = clampText;
